import { useEffect } from 'react';
import { useMobileOptimization } from '@/hooks/useMobileOptimization';

// Mobile-specific performance optimizations
export const MobilePerformanceOptimizer = () => {
  const { isMobile, isTablet } = useMobileOptimization();

  useEffect(() => {
    if (!isMobile && !isTablet) return;

    // Disable hover effects on touch devices
    const disableHover = () => {
      document.body.classList.add('touch-device');
    };

    // Optimize scroll performance
    const optimizeScrolling = () => {
      // Add momentum scrolling for iOS
      (document.body.style as any).webkitOverflowScrolling = 'touch';
      
      // Reduce scroll events frequency
      let ticking = false;
      const handleScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    };

    // Optimize touch events
    const optimizeTouch = () => {
      // Add touch-action CSS for better touch handling
      const style = document.createElement('style');
      style.textContent = `
        .touch-optimized {
          touch-action: manipulation;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
        }
        
        .touch-device *:hover {
          animation: none !important;
          transition: none !important;
        }
        
        @media (max-width: 768px) {
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
          
          .mobile-optimized {
            will-change: transform;
            backface-visibility: hidden;
            perspective: 1000px;
          }
        }
        
        .mobile-touch-target {
          min-height: 48px;
          min-width: 48px;
        }
      `;
      document.head.appendChild(style);
      
      return () => document.head.removeChild(style);
    };

    // Apply optimizations
    disableHover();
    const cleanupScroll = optimizeScrolling();
    const cleanupTouch = optimizeTouch();

    return () => {
      cleanupScroll?.();
      cleanupTouch?.();
    };
  }, [isMobile, isTablet]);

  return null; // This is a utility component with no visual output
};